import React, { useState, useMemo } from 'react'
import { inertiaSave, usePricingReview } from '@/hooks/usePricingReview'
import OperatorLayout from '@/layouts/OperatorLayout'
import PricingRulesNav from '@/components/pricing-rules/PricingRulesNav'
import { HelpTooltip } from '@/components/HelpTooltip'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import type { SharedProps } from '@/types/shared'

interface GapFillingProps extends SharedProps {
  orphanDayDiscount: number | null
  adjacentDays: number
  adjacentModifier: number
}

export default function GapFilling({
  currentUser,
  property,
  properties,
  orphanDayDiscount: initialOrphanDiscount,
  adjacentDays: initialAdjacentDays,
  adjacentModifier: initialAdjacentModifier,
}: GapFillingProps) {
  const base = property.basePath

  // Orphan day
  const [orphanEnabled, setOrphanEnabled] = useState(initialOrphanDiscount != null && initialOrphanDiscount > 0)
  const [orphanDiscount, setOrphanDiscount] = useState((initialOrphanDiscount ?? 10).toString())

  // Adjacent day
  const [adjacentEnabled, setAdjacentEnabled] = useState(initialAdjacentDays > 0)
  const [adjacentDays, setAdjacentDays] = useState(initialAdjacentDays.toString())
  const [adjacentModifier, setAdjacentModifier] = useState(initialAdjacentModifier.toString())

  const [saving, setSaving] = useState(false)
  const review = usePricingReview()

  const isDirty = useMemo(() => {
    if (orphanEnabled !== (initialOrphanDiscount != null && initialOrphanDiscount > 0)) return true
    if (orphanDiscount !== (initialOrphanDiscount ?? 10).toString()) return true
    if (adjacentEnabled !== (initialAdjacentDays > 0)) return true
    if (adjacentDays !== initialAdjacentDays.toString()) return true
    if (adjacentModifier !== initialAdjacentModifier.toString()) return true
    return false
  }, [orphanEnabled, orphanDiscount, adjacentEnabled, adjacentDays, adjacentModifier, initialOrphanDiscount, initialAdjacentDays, initialAdjacentModifier])

  // Reviewed on the server first (change safety R1.5), then sent by the same PATCH.
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const payload = {
      orphan_day_discount: orphanEnabled ? Number(orphanDiscount) || 0 : null,
      adjacent_days: adjacentEnabled ? Number(adjacentDays) || 0 : 0,
      adjacent_modifier: adjacentEnabled ? Number(adjacentModifier) || 0 : 0,
    }
    review.open(`${base}/pricing-rules/gap-filling/review`, payload, () =>
      inertiaSave('patch', `${base}/pricing-rules/gap-filling`, payload, {
        onStart: () => setSaving(true),
        onFinish: () => setSaving(false),
      })
    )
  }

  // Visual preview data
  const orphanPct = orphanEnabled ? Number(orphanDiscount) || 0 : 0
  const adjDays = adjacentEnabled ? Number(adjacentDays) || 0 : 0
  const adjMod = adjacentEnabled ? Number(adjacentModifier) || 0 : 0

  // Build a 7-day preview: [adj...] [gap] [adj...]
  const previewDays = useMemo(() => {
    const days: Array<{ label: string; type: 'booked' | 'orphan' | 'adjacent' | 'normal'; modifier: number }> = []
    // Pattern: Booked | Adjacent? | Orphan | Adjacent? | Booked | Normal | Normal
    const total = 3 + adjDays * 2
    const padded = Math.max(7, total + 2)

    for (let i = 0; i < padded; i++) {
      const dayLabel = `Day ${i + 1}`
      if (i === 0) {
        days.push({ label: dayLabel, type: 'booked', modifier: 0 })
      } else if (i > 0 && i <= adjDays) {
        days.push({ label: dayLabel, type: 'adjacent', modifier: -adjMod })
      } else if (i === adjDays + 1) {
        days.push({ label: dayLabel, type: 'orphan', modifier: -orphanPct })
      } else if (i > adjDays + 1 && i <= adjDays * 2 + 1) {
        days.push({ label: dayLabel, type: 'adjacent', modifier: -adjMod })
      } else if (i === adjDays * 2 + 2) {
        days.push({ label: dayLabel, type: 'booked', modifier: 0 })
      } else {
        days.push({ label: dayLabel, type: 'normal', modifier: 0 })
      }
    }
    return days
  }, [orphanPct, adjDays, adjMod])

  return (
    <OperatorLayout title="Gap Filling" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        <PricingRulesNav
          propertyBasePath={property.basePath}
          activeTab="gap-filling"
          help={{
            content:
              "Gap filling helps sell single-night openings between bookings. An 'orphan day' is a lone empty night sandwiched between occupied nights — discounting it encourages guests to fill the gap rather than leaving revenue on the table.",
          }}
        />

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Visual Preview */}
          {(orphanEnabled || adjacentEnabled) && (
            <div className="animate-slide-in rounded-xl border border-border/60 bg-white p-5 shadow-sm" style={{ animationDelay: '100ms' }}>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">How It Works</h3>
              <p className="text-xs text-muted-foreground mb-4">
                When a single unbooked night appears between two booked nights, the engine applies your discount to make it more attractive.
              </p>
              <div className="flex items-end gap-1">
                {previewDays.map((day, i) => {
                  const height = day.type === 'booked' ? 'h-12' :
                    day.type === 'orphan' ? 'h-6' :
                    day.type === 'adjacent' ? 'h-9' : 'h-10'
                  return (
                    <div key={i} className="flex flex-col items-center flex-1 gap-1">
                      {day.modifier !== 0 && (
                        <span className={cn('text-[10px] font-semibold tabular-nums', day.modifier < 0 ? 'text-rose-500' : 'text-emerald-500')}>
                          {day.modifier}%
                        </span>
                      )}
                      <div className={cn(
                        'w-full rounded-md transition-all',
                        height,
                        day.type === 'booked' && 'bg-primary/20 border border-primary/30',
                        day.type === 'orphan' && 'bg-rose-100 border border-rose-300 border-dashed',
                        day.type === 'adjacent' && 'bg-amber-100 border border-amber-300',
                        day.type === 'normal' && 'bg-muted/40 border border-border/40',
                      )} />
                      <span className="text-[9px] text-muted-foreground">
                        {day.type === 'booked' ? 'Booked' :
                         day.type === 'orphan' ? 'Gap' :
                         day.type === 'adjacent' ? 'Adj.' : ''}
                      </span>
                    </div>
                  )
                })}
              </div>
              <div className="flex items-center gap-4 mt-3 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-4 rounded bg-primary/20 border border-primary/30" /> Booked</span>
                {orphanEnabled && <span className="flex items-center gap-1"><span className="inline-block h-2 w-4 rounded bg-rose-100 border border-dashed border-rose-300" /> Orphan gap ({orphanPct}% discount)</span>}
                {adjacentEnabled && <span className="flex items-center gap-1"><span className="inline-block h-2 w-4 rounded bg-amber-100 border border-amber-300" /> Adjacent ({adjMod}% discount)</span>}
              </div>
            </div>
          )}

          {/* Orphan Day Discount */}
          <div className="animate-slide-in rounded-xl border border-border/60 bg-white p-6 shadow-sm" style={{ animationDelay: '150ms' }}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="flex items-center gap-1.5">
                  <h3 className="text-sm font-semibold text-foreground">Orphan Day Discount</h3>
                  <HelpTooltip
                    content="An orphan day is a single unbooked night between two booked nights. For example: Guest A checks out Monday, Guest B checks in Wednesday — Tuesday is the orphan. This discount makes Tuesday cheaper to encourage a booking that fills the gap."
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Reduce rates for single-night gaps between bookings. The discount is applied as a percentage reduction from the computed rate.
                </p>
              </div>
              <Switch checked={orphanEnabled} onCheckedChange={setOrphanEnabled} />
            </div>
            {orphanEnabled && (
              <div className="mt-4 flex items-center gap-3">
                <Label className="text-sm text-muted-foreground">Discount</Label>
                <div className="relative w-24">
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    max="50"
                    className="pr-6 text-center font-semibold"
                    value={orphanDiscount}
                    onChange={(e) => setOrphanDiscount(e.target.value)}
                  />
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  e.g., a {orphanPct}% discount on a £100 rate = £{(100 - orphanPct).toFixed(0)} for the orphan night
                </span>
              </div>
            )}
          </div>

          {/* Adjacent Day Modifier */}
          <div className="animate-slide-in rounded-xl border border-border/60 bg-white p-6 shadow-sm" style={{ animationDelay: '200ms' }}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="flex items-center gap-1.5">
                  <h3 className="text-sm font-semibold text-foreground">Adjacent Day Modifier</h3>
                  <HelpTooltip
                    content="Days immediately before and after an orphan gap. A smaller discount on these days creates a smoother price transition instead of one dramatic dip. This makes the overall pricing look more natural and can encourage longer stays."
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Apply a partial discount to the days surrounding an orphan gap for smoother pricing transitions. This avoids a sharp price cliff between the gap and surrounding days.
                </p>
              </div>
              <Switch checked={adjacentEnabled} onCheckedChange={setAdjacentEnabled} />
            </div>
            {adjacentEnabled && (
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-muted-foreground whitespace-nowrap">Days each side</Label>
                  <Input
                    type="number"
                    step="1"
                    min="1"
                    max="3"
                    className="w-16 text-center font-semibold"
                    value={adjacentDays}
                    onChange={(e) => setAdjacentDays(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-muted-foreground whitespace-nowrap">Discount</Label>
                  <div className="relative w-24">
                    <Input
                      type="number"
                      step="1"
                      className="pr-6 text-center font-semibold"
                      value={adjacentModifier}
                      onChange={(e) => setAdjacentModifier(e.target.value)}
                    />
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">
                  {adjDays} day{adjDays !== 1 ? 's' : ''} before and after the gap at {adjMod}% discount
                </span>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={!isDirty || saving} className="rounded-lg px-6">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
      {review.dialog}
    </OperatorLayout>
  )
}
